import React, { useEffect, useMemo, useRef, useState } from "react";
import { useParams, Link as RouterLink } from "react-router-dom";
import Navbar from "../components/Navbar";
import useAxios from "../utils/useAxios";
import RenderPolygon from "../components/map/RenderPolygon";
import Map, {
  GeolocateControl,
  FullscreenControl,
  NavigationControl,
  ScaleControl,
  type MapRef,
} from "react-map-gl";
import {
  Box,
  Center,
  Flex,
  Heading,
  Link,
  Skeleton,
  Spinner,
  Text,
  RadioGroup,
  Radio,
  Stack,
  Select,
  Button,
  useToast,
  Slider,
  SliderTrack,
  SliderFilledTrack,
  SliderThumb,
  RangeSlider,
  RangeSliderTrack,
  RangeSliderFilledTrack,
  RangeSliderThumb,
} from "@chakra-ui/react";
import { IField } from "../types/fieldInterface";

// NOTE: This view renders the GeoTIFF directly in-browser onto a Canvas
// and overlays it with a Mapbox "canvas" source (no server tiling).
// You will need the geotiff package: npm install geotiff

type IndexName = "NDVI" | "NDWI";

interface SatelliteEntry {
  satellite_data_id: number;
  request_id: number;
  created_at: string; // ISO
  ingestion_date: string; // ISO
  indices: Record<string, string>; // index name -> GeoTIFF URL
  download_urls: Record<string, string>; // index name -> download URL
}

const CANVAS_SOURCE_ID = "satellite-canvas-src";
const CANVAS_LAYER_ID = "satellite-canvas-layer";

// Color helpers and profiles
type RGBA = [number, number, number, number];
const clamp01 = (x: number) => (x < 0 ? 0 : x > 1 ? 1 : x);
const lerp = (a: number, b: number, t: number) => a + (b - a) * t;
const lerpColor = (
  c1: [number, number, number],
  c2: [number, number, number],
  t: number
): [number, number, number] => [
  Math.round(lerp(c1[0], c2[0], t)),
  Math.round(lerp(c1[1], c2[1], t)),
  Math.round(lerp(c1[2], c2[2], t)),
];

// Existing NDVI palette (Red -> Yellow -> Green)
const ndviColor = (t: number): RGBA => {
  const tt = clamp01(Number.isFinite(t) ? t : 0);
  let r = 0,
    g = 0,
    b = 0;
  if (tt < 0.5) {
    const u = tt * 2; // 0..1
    // Red -> Yellow
    r = 255;
    g = Math.round(lerp(0, 255, u));
    b = 0;
  } else {
    const u = (tt - 0.5) * 2; // 0..1
    // Yellow -> Green
    r = Math.round(lerp(255, 0, u));
    g = Math.round(lerp(255, 180, u)); // slightly bright green
    b = 0;
  }
  const a = 220;
  return [r, g, b, a];
};

// NDWI palette (dry -> neutral -> wet) : Brown -> Light -> BlueGreen
// Uses a diverging scheme better suited for water indices
const ndwiColor = (v: number, min: number, max: number): RGBA => {
  // ColorBrewer-inspired BrBG endpoints
  const dry: [number, number, number] = [140, 81, 10]; // #8c510a
  const mid: [number, number, number] = [245, 245, 245]; // #f5f5f5
  const wet: [number, number, number] = [1, 102, 94]; // #01665e

  if (!Number.isFinite(v)) return [0, 0, 0, 0];
  if (v >= 0) {
    const posRange = Math.max(1e-6, max - 0);
    const u = clamp01((v - 0) / posRange);
    const [r, g, b] = lerpColor(mid, wet, u);
    return [r, g, b, 220];
  } else {
    const negRange = Math.max(1e-6, 0 - min);
    const u = clamp01((v - min) / negRange);
    const [r, g, b] = lerpColor(dry, mid, u);
    return [r, g, b, 220];
  }
};

const FieldSatelliteView: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const api = useAxios();
  const toast = useToast();
  const mapRef = useRef<MapRef>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);

  const [field, setField] = useState<IField | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [loadingRaster, setLoadingRaster] = useState(false);
  const [entries, setEntries] = useState<SatelliteEntry[]>([]);
  const [indexName, setIndexName] = useState<IndexName>("NDVI");
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [rasterOpacity, setRasterOpacity] = useState<number>(0.85);
  const [valueRange, setValueRange] = useState<[number, number]>([-1, 1]);
  const [mapReady, setMapReady] = useState<boolean>(false);
  const rasterRef = useRef<{
    band: Float32Array | Uint16Array | Int16Array | Uint8Array;
    width: number;
    height: number;
    noDataVal?: number;
  } | null>(null);

  const selectedEntry = useMemo(() => {
    if (!entries.length) return undefined;
    if (selectedId == null) return entries[0];
    return entries.find((e) => e.satellite_data_id === selectedId) || entries[0];
  }, [entries, selectedId]);

  const selectedTiffUrl = selectedEntry?.indices?.[indexName];
  const selectedDownloadUrl = selectedEntry?.download_urls?.[indexName];

  useEffect(() => {
    let ignore = false;
    (async () => {
      try {
        setIsLoading(true);
        const [fieldRes, satRes] = await Promise.all([
          api.get<IField>(`agtech/fields/${id}/`),
          api.get<SatelliteEntry[]>(`satellite-data/by-field/${id}/`),
        ]);
        if (ignore) return;
        setField(fieldRes.data);
        const list = [...satRes.data].sort(
          (a, b) => new Date(b.ingestion_date).getTime() - new Date(a.ingestion_date).getTime()
        );
        setEntries(list);
        setSelectedId(list[0]?.satellite_data_id ?? null);
      } catch (err: any) {
        if (err.response?.status !== 401) {
          toast({
            title: "Failed to load satellite data",
            status: "error",
            duration: 6000,
            isClosable: true,
          });
        }
      } finally {
        if (!ignore) setIsLoading(false);
      }
    })();
    return () => {
      ignore = true;
    };
  }, [id]);

  const initialViewState = useMemo(() => {
    if (field?.field_center_lon && field?.field_center_lat) {
      return {
        longitude: field.field_center_lon,
        latitude: field.field_center_lat,
        zoom: 15,
        bearing: 0,
        pitch: 0,
      } as const;
    }
    return { longitude: 10, latitude: 50, zoom: 4, bearing: 0, pitch: 0 } as const;
  }, [field?.field_center_lon, field?.field_center_lat]);

  // Render the selected GeoTIFF into a canvas and overlay on the map
  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      const url = selectedTiffUrl;
      if (!url || !mapReady) return;
      const map = mapRef.current?.getMap();
      if (!map) return;
      setLoadingRaster(true);
      try {
        // Lazy import geotiff to avoid bundling issues if view is unused
        const geotiff = await import("geotiff");
        const res = await fetch(url, { cache: "no-store" });
        if (!res.ok) throw new Error(`Failed to fetch GeoTIFF (${res.status})`);
        const arrayBuffer = await res.arrayBuffer();
        const tiff = await geotiff.fromArrayBuffer(arrayBuffer);
        const image = await tiff.getImage();
        const bbox = image.getBoundingBox(); // [minX, minY, maxX, maxY]
        const width = image.getWidth();
        const height = image.getHeight();

        // Read first band (typical for NDVI/NDWI). If multi-band, use first.
        const raster = await image.readRasters({ samples: [0], interleave: false });
        const band = raster[0] as Float32Array | Uint16Array | Int16Array | Uint8Array;

        // Create canvas and draw a simple pseudo-color
        const canvas =
          canvasRef.current ||
          (document.getElementById("satellite-canvas") as HTMLCanvasElement) ||
          document.createElement("canvas");
        canvas.id = "satellite-canvas";
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext("2d");
        if (!ctx) throw new Error("Canvas 2D not supported");
        const imgData = ctx.createImageData(width, height);

        // Use adjustable range from UI (default [-1, 1])
        const [min, max] = valueRange;
        const range = Math.max(1e-6, max - min);

        // Attempt to read NoData value from TIFF (GDAL)
        const noDataRaw =
          (image as any).fileDirectory?.GDAL_NODATA ?? (image as any).getGDALNoData?.();
        const noDataVal: number | undefined =
          typeof noDataRaw === "number"
            ? noDataRaw
            : typeof noDataRaw === "string" && Number.isFinite(parseFloat(noDataRaw))
            ? parseFloat(noDataRaw)
            : undefined;

        // Cache raster for quick re-coloring without re-fetching
        rasterRef.current = { band, width, height, noDataVal };

        for (let i = 0; i < band.length; i++) {
          const vRaw = (band as any)[i];
          const v = typeof vRaw === "number" ? vRaw : Number(vRaw);

          // Transparent for invalid or NoData values
          if (
            !Number.isFinite(v) ||
            (noDataVal !== undefined && v === noDataVal) ||
            v === -9999 ||
            v === 32767 ||
            v === 65535 ||
            v < -1.5 ||
            v > 1.5
          ) {
            const j = i * 4;
            imgData.data[j] = 0;
            imgData.data[j + 1] = 0;
            imgData.data[j + 2] = 0;
            imgData.data[j + 3] = 0; // fully transparent
            continue;
          }

          let t = (v - min) / range;
          if (!Number.isFinite(t)) t = 0;
          if (t < 0) t = 0;
          if (t > 1) t = 1;

          // Choose color profile based on index
          let rC: number, gC: number, bC: number, aC: number;
          if (indexName === "NDWI") {
            [rC, gC, bC, aC] = ndwiColor(v, min, max);
          } else {
            [rC, gC, bC, aC] = ndviColor(t);
          }

          const j = i * 4;
          imgData.data[j] = rC;
          imgData.data[j + 1] = gC;
          imgData.data[j + 2] = bC;
          imgData.data[j + 3] = aC;
        }
        // GeoTIFF origin is typically top-left. If needed, flip vertically.
        ctx.putImageData(imgData, 0, 0);
        // Ask map to repaint so canvas source updates
        map.triggerRepaint();

        // Store canvas
        canvasRef.current = canvas;

        if (cancelled) return;

        // Compute image corner coordinates in source CRS
        const [minX, minY, maxX, maxY] = bbox;
        const cornersSrc: [number, number][] = [
          [minX, maxY],
          [maxX, maxY],
          [maxX, minY],
          [minX, minY],
        ];

        // Transform to WGS84 based on GeoKeys
        let transformXY = (x: number, y: number): [number, number] => [x, y];
        try {
          const geoKeys: any = (image as any).getGeoKeys ? (image as any).getGeoKeys() : undefined;
          const projCode: number | undefined =
            geoKeys?.ProjectedCSTypeGeoKey || geoKeys?.GeographicTypeGeoKey;

          const toWgsFrom3857 = (x: number, y: number): [number, number] => {
            const R = 6378137;
            const lon = (x / R) * (180 / Math.PI);
            const lat = (2 * Math.atan(Math.exp(y / R)) - Math.PI / 2) * (180 / Math.PI);
            return [lon, lat];
          };

          const utmDefFromEpsg = (code: number): { def: string; name: string } | null => {
            if (code >= 32601 && code <= 32660) {
              const zone = code - 32600;
              return {
                def: `+proj=utm +zone=${zone} +datum=WGS84 +units=m +no_defs +type=crs`,
                name: `EPSG:${code}`,
              };
            }
            if (code >= 32701 && code <= 32760) {
              const zone = code - 32700;
              return {
                def: `+proj=utm +zone=${zone} +south +datum=WGS84 +units=m +no_defs +type=crs`,
                name: `EPSG:${code}`,
              };
            }
            return null;
          };

          if (projCode === 4326) {
            transformXY = (x, y) => [x, y];
          } else if (projCode === 3857 || projCode === 102100 || projCode === 900913) {
            transformXY = toWgsFrom3857;
          } else if (typeof projCode === "number") {
            try {
              const proj4mod: any = await import("proj4");
              const proj4 = proj4mod.default || proj4mod;
              const utm = utmDefFromEpsg(projCode);
              if (utm) {
                if (!proj4.defs[utm.name]) {
                  proj4.defs(utm.name, utm.def);
                }
                transformXY = (x, y) => {
                  const out = proj4(utm.name, "EPSG:4326", [x, y]);
                  return [out[0], out[1]];
                };
              } else {
                const from = `EPSG:${projCode}`;
                transformXY = (x, y) => {
                  const out = proj4(from, "EPSG:4326", [x, y]);
                  return [out[0], out[1]];
                };
              }
            } catch {
              const guessMerc =
                Math.max(Math.abs(minX), Math.abs(minY), Math.abs(maxX), Math.abs(maxY)) > 180;
              transformXY = guessMerc ? toWgsFrom3857 : (x, y) => [x, y];
            }
          } else {
            const guessMerc =
              Math.max(Math.abs(minX), Math.abs(minY), Math.abs(maxX), Math.abs(maxY)) > 180;
            transformXY = guessMerc ? toWgsFrom3857 : (x, y) => [x, y];
          }
        } catch {
          transformXY = (x, y) => [x, y];
        }

        const coordinates = cornersSrc.map(([x, y]) => transformXY(x, y)) as [number, number][];
        // Validate; if invalid latitudes, try Mercator fallback
        if (coordinates.some(([, lat]) => lat < -90 || lat > 90)) {
          const toWgsFrom3857 = (x: number, y: number): [number, number] => {
            const R = 6378137;
            const lon = (x / R) * (180 / Math.PI);
            const lat = (2 * Math.atan(Math.exp(y / R)) - Math.PI / 2) * (180 / Math.PI);
            return [lon, lat];
          };
          const fallback = cornersSrc.map(([x, y]) => toWgsFrom3857(x, y)) as [number, number][];
          if (!fallback.some(([, lat]) => lat < -90 || lat > 90)) {
            coordinates.splice(0, coordinates.length, ...fallback);
          }
        }

        const ensureAdded = () => {
          const src: any = map.getSource(CANVAS_SOURCE_ID);
          if (!src) {
            map.addSource(CANVAS_SOURCE_ID, {
              type: "canvas",
              // Use id string so Mapbox can find it
              canvas: canvas.id,
              coordinates: coordinates,
              animate: true,
            } as any);
            if (!map.getLayer(CANVAS_LAYER_ID)) {
              map.addLayer({
                id: CANVAS_LAYER_ID,
                type: "raster",
                source: CANVAS_SOURCE_ID,
                paint: { "raster-opacity": 0.85 },
              });
            }
          } else {
            // @ts-ignore
            src.setCoordinates(coordinates);
          }
        };

        if (map.isStyleLoaded()) {
          ensureAdded();
        } else {
          map.once("load", ensureAdded);
        }
      } catch (e) {
        console.error(e);
        toast({
          title: "Failed to render GeoTIFF",
          status: "error",
          duration: 6000,
          isClosable: true,
        });
      } finally {
        setLoadingRaster(false);
      }
    };
    run();
    return () => {
      cancelled = true;
    };
  }, [selectedTiffUrl, toast, mapReady]);

  // Re-colorize the existing raster when the value range changes
  useEffect(() => {
    const map = mapRef.current?.getMap();
    const r = rasterRef.current;
    const canvas =
      canvasRef.current ||
      (document.getElementById("satellite-canvas") as HTMLCanvasElement | null);
    if (!map || !r || !canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    const { band, width, height, noDataVal } = r;
    const imgData = ctx.createImageData(width, height);
    const [min, max] = valueRange;
    const range = Math.max(1e-6, max - min);
    for (let i = 0; i < band.length; i++) {
      const vRaw = (band as any)[i];
      const v = typeof vRaw === "number" ? vRaw : Number(vRaw);
      if (
        !Number.isFinite(v) ||
        (noDataVal !== undefined && v === noDataVal) ||
        v === -9999 ||
        v === 32767 ||
        v === 65535 ||
        v < -1.5 ||
        v > 1.5
      ) {
        const j = i * 4;
        imgData.data[j] = 0;
        imgData.data[j + 1] = 0;
        imgData.data[j + 2] = 0;
        imgData.data[j + 3] = 0;
        continue;
      }
      let t = (v - min) / range;
      if (!Number.isFinite(t)) t = 0;
      if (t < 0) t = 0;
      if (t > 1) t = 1;

      let rC: number, gC: number, bC: number, aC: number;
      if (indexName === "NDWI") {
        [rC, gC, bC, aC] = ndwiColor(v, min, max);
      } else {
        [rC, gC, bC, aC] = ndviColor(t);
      }

      const j = i * 4;
      imgData.data[j] = rC;
      imgData.data[j + 1] = gC;
      imgData.data[j + 2] = bC;
      imgData.data[j + 3] = aC;
    }
    ctx.putImageData(imgData, 0, 0);
    map.triggerRepaint();
  }, [valueRange, indexName]);

  useEffect(() => {
    const map = mapRef.current?.getMap();
    if (!map) return;
    if (map.getLayer(CANVAS_LAYER_ID)) {
      try {
        map.setPaintProperty(CANVAS_LAYER_ID, "raster-opacity", rasterOpacity);
      } catch (e) {
        // ignore if layer not ready yet
      }
    }
  }, [rasterOpacity]);

  useEffect(() => {
    return () => {
      const map = mapRef.current?.getMap();
      if (!map) return;
      if (map.getLayer(CANVAS_LAYER_ID)) {
        map.removeLayer(CANVAS_LAYER_ID);
      }
      if (map.getSource(CANVAS_SOURCE_ID)) {
        map.removeSource(CANVAS_SOURCE_ID);
      }
    };
  }, []);

  return (
    <Navbar>
      <Box maxW="1200px" mx="auto">
        <Flex align="center" justify="space-between" py={4} px={2}>
          <Heading size="md">Field Satellite</Heading>
          <Link as={RouterLink} to={field ? `/fields/view/${field.id}` : "/fields"}>
            Back to field
          </Link>
        </Flex>

        <Flex
          gap={4}
          align={{ base: "stretch", md: "center" }}
          direction={{ base: "column", md: "row" }}
          px={2}
          pb={2}
        >
          <Box>
            <Text fontWeight="bold" mb={1}>
              Index
            </Text>
            <RadioGroup value={indexName} onChange={(v) => setIndexName(v as IndexName)}>
              <Stack direction="row">
                <Radio value="NDVI">NDVI</Radio>
                <Radio value="NDWI">NDWI</Radio>
              </Stack>
            </RadioGroup>
          </Box>

          <Box minW={{ base: "100%", md: "320px" }}>
            <Text fontWeight="bold" mb={1}>
              Ingestion date
            </Text>
            <Select
              value={selectedId ?? undefined}
              onChange={(e) => setSelectedId(Number(e.target.value))}
            >
              {entries.map((e) => (
                <option key={e.satellite_data_id} value={e.satellite_data_id}>
                  {new Date(e.ingestion_date).toLocaleString()}
                </option>
              ))}
            </Select>
          </Box>
        </Flex>

        <Box height={{ base: 500, lg: 700 }}>
          {isLoading ? (
            <Center h="100%">
              <Skeleton w="100%" h="100%" />
            </Center>
          ) : !entries.length ? (
            <Center h="100%">
              <Text>No satellite data available for this field.</Text>
            </Center>
          ) : (
            <Map
              ref={mapRef}
              mapLib={import("mapbox-gl")}
              mapStyle="mapbox://styles/mapbox/satellite-v9"
              initialViewState={initialViewState}
              mapboxAccessToken={process.env.REACT_APP_MAPBOX_TOKEN}
              style={{ width: "100%", height: "100%" }}
              onLoad={() => setMapReady(true)}
            >
              {field && <RenderPolygon field={field} />}
              <GeolocateControl />
              <FullscreenControl />
              <NavigationControl />
              <ScaleControl />
            </Map>
          )}
          <canvas
            id="satellite-canvas"
            style={{ position: "absolute", visibility: "hidden", width: 0, height: 0 }}
          />
          {loadingRaster && (
            <Flex
              position="absolute"
              bottom={4}
              left={4}
              bg="blackAlpha.600"
              color="white"
              px={3}
              py={2}
              borderRadius="md"
              align="center"
              gap={2}
            >
              <Spinner size="sm" />
              <Text>Rendering raster…</Text>
            </Flex>
          )}
        </Box>

        <Box px={2} pt={3}>
          <Text fontWeight="bold" mb={1}>
            Value range
          </Text>
          <Flex align="center" gap={3}>
            <Text w="48px" textAlign="right">
              {valueRange[0].toFixed(2)}
            </Text>
            <RangeSlider
              aria-label={["min-value", "max-value"]}
              min={-1}
              max={1}
              step={0.01}
              value={valueRange}
              onChange={(vals) => setValueRange(vals as [number, number])}
              minStepsBetweenThumbs={1}
            >
              <RangeSliderTrack>
                <RangeSliderFilledTrack />
              </RangeSliderTrack>
              <RangeSliderThumb index={0} />
              <RangeSliderThumb index={1} />
            </RangeSlider>
            <Text w="48px" textAlign="left">
              {valueRange[1].toFixed(2)}
            </Text>
          </Flex>
        </Box>

        <Box px={2} pt={3}>
          <Text fontWeight="bold" mb={1}>
            Opacity
          </Text>
          <Flex align="center" gap={3}>
            <Slider
              aria-label="opacity-slider"
              min={0}
              max={1}
              step={0.05}
              value={rasterOpacity}
              onChange={(v) => setRasterOpacity(v)}
            >
              <SliderTrack>
                <SliderFilledTrack />
              </SliderTrack>
              <SliderThumb />
            </Slider>
            <Text w="40px" textAlign="right">
              {Math.round(rasterOpacity * 100)}%
            </Text>
          </Flex>
        </Box>
      </Box>
    </Navbar>
  );
};

export default FieldSatelliteView;
